/**
 * Email Search Controller — signal-store-backed search, filter tabs, sort, chips.
 *
 * Delegates all state to the central emailStore.
 * On every filter change: writes signals → calls store.reloadList()
 * which rebuilds the email_list Turbo Frame URL from current signal values.
 */

import { Controller } from '@hotwired/stimulus'
import { effect } from '@preact/signals'

const DEBOUNCE_MS = 350

export default class extends Controller {
  static targets = ['input', 'filters', 'filterToggle']

  connect() {
    this._timer    = null
    this._disposers = []

    const store = this._store()
    if (!store) return

    // ── Effect: sync filtersOpen signal → DOM visibility ──────────────────
    this._disposers.push(
      effect(() => {
        if (!this.hasFiltersTarget) return
        this.filtersTarget.classList.toggle('d-none', !store.filtersOpen.value)
      })
    )

    // ── Effect: sync quickFilter to chip button active state ──────────────
    this._disposers.push(
      effect(() => {
        const qf = store.quickFilter.value
        this.element.querySelectorAll('[data-email-search-chip-param]').forEach(btn => {
          btn.classList.toggle('active', btn.dataset.emailSearchChipParam === qf)
        })
      })
    )
  }

  disconnect() {
    clearTimeout(this._timer)
    this._disposers.forEach(d => d())
    this._disposers = []
  }

  // ── Search input (debounced) ──────────────────────────────────────────────

  search() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this._applyAndReload(), DEBOUNCE_MS)
  }

  submitSearch(e) {
    e?.preventDefault()
    clearTimeout(this._timer)
    this._applyAndReload()
  }

  clearSearch() {
    if (this.hasInputTarget) this.inputTarget.value = ''
    const store = this._store()
    if (store) store.setSearchText('')
    this._applyAndReload()
  }

  // ── Advanced filter panel ─────────────────────────────────────────────────

  toggleFilters() {
    const store = this._store()
    if (store) store.toggleFiltersPanel()
  }

  applyFilter() {
    this._applyAndReload()
  }

  // ── Filter tabs (All / Unread) ────────────────────────────────────────────

  setTab(e) {
    const tab   = e.params.tab
    const store = this._store()
    if (store) store.setQuickFilter(tab === 'unread' ? 'unread' : '')
    this._applyAndReload()
  }

  // ── Quick filter chips ────────────────────────────────────────────────────

  toggleChip(e) {
    const chip  = e.params.chip
    const store = this._store()
    if (store) store.toggleQuickFilter(chip)
    this._applyAndReload()
  }

  // ── Sort dropdown ─────────────────────────────────────────────────────────

  setSort(e) {
    const sort  = e.params.sort
    const store = this._store()
    if (store) store.setSort(sort)
    this._applyAndReload()
  }

  // ── Clear all ─────────────────────────────────────────────────────────────

  clearAllFilters() {
    const store = this._store()
    if (store) store.clearAllFilters()
    if (this.hasInputTarget) this.inputTarget.value = ''
    if (this.hasFiltersTarget) {
      this.filtersTarget.querySelectorAll('select').forEach(el => { el.selectedIndex = 0 })
    }
    this._applyAndReload()
  }

  // ── Core: sync state to store then reload ─────────────────────────────────

  _applyAndReload() {
    const store = this._store()
    if (!store) return

    // Sync DOM state into store signals
    if (this.hasInputTarget) {
      store.setSearchText(this.inputTarget.value.trim())
    }

    // Advanced filter selects
    if (this.hasFiltersTarget) {
      this.filtersTarget.querySelectorAll('select, input[type=text]').forEach(el => {
        if (el.name) store.setAdvancedFilter(el.name, el.value || '')
      })
    }

    store.reloadList()
  }

  _store() {
    return window._emailStore || null
  }
}
